import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { DEFAULT_WINNING_DISCOUNTS } from '../../utils/promoWheel';
import { fetchWheelCampaign, saveWheelCampaign } from '../../api/promoCodesApi';
import { getApiErrorText } from '../../utils/apiResponse';
import { promoDateInput, promoDateToUtc } from '../../utils/promoDates';

const emptyCampaign = () => ({ title: 'Колесо знижок', startsAt: '', expiresAt: '', isActive: false, winningDiscounts: [...DEFAULT_WINNING_DISCOUNTS] });

export default function AdminPromoWheelSettings() {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const saving = useRef(false);
  const request = useRef(0);

  useEffect(() => {
    if (!show) return;
    const sequence = ++request.current;
    setLoading(true); setError(''); setForm(null);
    fetchWheelCampaign().then(campaign => {
      if (sequence !== request.current) return;
      setForm(campaign ? { ...campaign, winningDiscounts: campaign.winningDiscounts ?? [...DEFAULT_WINNING_DISCOUNTS], startsAt: promoDateInput(campaign.startsAt), expiresAt: promoDateInput(campaign.expiresAt) } : emptyCampaign());
    }).catch(reason => {
      if (sequence === request.current) setError(getApiErrorText(reason));
    }).finally(() => { if (sequence === request.current) setLoading(false); });
    return () => { request.current += 1; };
  }, [show, refresh]);

  const open = () => {
    if (saving.current || show) return;
    setBusy(false); setLoading(true); setError(''); setForm(null); setShow(true);
  };
  const close = () => { if (!saving.current) setShow(false); };
  const update = (key, value) => setForm(previous => ({ ...previous, [key]: value }));
  const save = async event => {
    event.preventDefault();
    if (saving.current || !form || loading) return;
    setError('');
    let body;
    try {
      body = { ...form, title: form.title.trim(), startsAt: promoDateToUtc(form.startsAt), expiresAt: promoDateToUtc(form.expiresAt) };
      if (!body.winningDiscounts?.length) throw new Error('Оберіть хоча б один виграш: 4% або 5%');
      if (!body.startsAt || !body.expiresAt || body.startsAt >= body.expiresAt) throw new Error('Вкажіть початок і завершення акції у правильному порядку');
    } catch (reason) { setError(reason.message); return; }
    const sequence = request.current;
    saving.current = true; setBusy(true);
    try {
      await saveWheelCampaign(body);
      if (sequence === request.current) setShow(false);
    } catch (reason) {
      if (sequence === request.current) setError(getApiErrorText(reason));
    } finally {
      saving.current = false;
      if (sequence === request.current) setBusy(false);
    }
  };

  return <>
    <button type='button' className='btn btn-outline-secondary' onClick={open}>Налаштувати колесо</button>
    <Modal show={show} onHide={close} centered animation={false}
      backdrop={busy ? 'static' : true} keyboard={!busy} aria-labelledby='wheel-settings-title' className='promo-admin-modal'>
      <Modal.Header>
        <button type='button' className='close order-2' aria-label='Закрити' disabled={busy} onClick={close}><span aria-hidden='true'>×</span></button>
        <Modal.Title as='h5' id='wheel-settings-title'>Колесо знижок</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <p role='status'>Завантажуємо налаштування…</p>}
        {error && <div className='alert alert-danger' role='alert'>{error}
          {!form && <button className='btn btn-link btn-sm' type='button' onClick={() => setRefresh(value => value + 1)}>Оновити налаштування</button>}
        </div>}
        {form && <form id='wheel-settings-form' onSubmit={save}>
          <div className='form-group'><label htmlFor='wheel-title'>Назва акції колеса</label>
            <input id='wheel-title' className='form-control' maxLength={120} required autoFocus value={form.title} disabled={busy} onChange={event => update('title', event.target.value)} /></div>
          <div className='form-row'>
            <div className='form-group col-md-6'><label htmlFor='wheel-starts'>Початок акції (Київ)</label>
              <input id='wheel-starts' type='datetime-local' className='form-control' required value={form.startsAt} disabled={busy} onChange={event => update('startsAt', event.target.value)} /></div>
            <div className='form-group col-md-6'><label htmlFor='wheel-expires'>Завершення акції (Київ)</label>
              <input id='wheel-expires' type='datetime-local' className='form-control' required value={form.expiresAt} disabled={busy} onChange={event => update('expiresAt', event.target.value)} /></div>
          </div>
          <fieldset className='form-group'>
            <legend className='small font-weight-bold mb-2'>Знижки, які можуть випадати</legend>
            <div className='d-flex' style={{ gap: '24px' }}>
              {DEFAULT_WINNING_DISCOUNTS.map(value => <label key={value} className='d-flex align-items-center mb-0'>
                <input type='checkbox' className='mr-2' disabled={busy} checked={form.winningDiscounts.includes(value)}
                  onChange={event => update('winningDiscounts', event.target.checked
                    ? [...form.winningDiscounts, value].sort() : form.winningDiscounts.filter(prize => prize !== value))} />{value}%
              </label>)}
            </div>
          </fieldset>
          <label className='d-block mb-0'><input type='checkbox' className='mr-2' checked={Boolean(form.isActive)} disabled={busy} onChange={event => update('isActive', event.target.checked)} />Акція колеса увімкнена</label>
        </form>}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-secondary' disabled={busy} onClick={close}>Скасувати</button>
        <button type='submit' form='wheel-settings-form' className='btn btn-dark' disabled={busy || loading || !form}>{busy ? 'Зберігаємо…' : 'Зберегти акцію'}</button>
      </Modal.Footer>
    </Modal>
  </>;
}
